import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import clientDB from '../config/clientDatabase';
import './Navigation.css';

const Navigation = ({ user, company, signOut }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pendingUsersCount, setPendingUsersCount] = useState(0);
  const location = useLocation();
  const isAdmin = company?.type === 'admin';

  useEffect(() => {
    if (isAdmin) {
      // Check for pending users periodically
      const checkPendingUsers = () => {
        const pendingUsers = clientDB.getPendingUsers();
        setPendingUsersCount(pendingUsers.length);
      };

      checkPendingUsers();
      const interval = setInterval(checkPendingUsers, 30000); // Check every 30 seconds

      return () => clearInterval(interval);
    }
  }, [isAdmin]);

  // Get user's display name
  const getUserDisplayName = () => {
    // First try to get name from user attributes
    if (user?.attributes?.name) {
      return user.attributes.name;
    }

    // Then try to get name from email (before @ symbol)
    const email = user?.attributes?.email || user?.username;
    if (email && email.includes('@')) {
      const emailName = email.split('@')[0];
      // Capitalize first letter and replace dots/underscores with spaces
      return emailName.charAt(0).toUpperCase() + emailName.slice(1).replace(/[._]/g, ' ');
    }

    return 'User';
  };

  const adminNavItems = [
    { path: '/dashboard', label: 'Dashboard' },
    { path: '/clients', label: 'Clients' },
    { path: '/users', label: 'Users' },
    { path: '/tasks', label: 'My Tasks' },
    { path: '/sync', label: 'Folder Sync' },
    { path: '/upload', label: 'Upload' },
    { path: '/reports', label: 'Reports' },
    { path: '/settings', label: 'Settings' }
  ];

  const clientNavItems = [
    { path: '/files', label: 'Files' },
    { path: '/upload', label: 'Upload Assets' },
    { path: '/reports', label: 'Reports' },
    { path: '/team', label: 'Team' },
    { path: '/settings', label: 'Settings' }
  ];

  const navItems = isAdmin ? adminNavItems : clientNavItems;

  const isActive = (path) => location.pathname === path;


  return (
    <>
      {/* Desktop Navigation */}
      <nav className="navigation-desktop">
        <div className="nav-container">
          <div className="nav-brand">
            <Link to="/" className="logo-container">
              <img src="/1209-digital-logo.svg" alt="1209 Digital" className="company-logo" />
              <div className="divider" />
              {company?.branding?.logo ? (
                <img src={company.branding.logo} alt={company.name} className="nav-logo" />
              ) : (
                <span className="nav-company-name">{company?.name || 'Report Portal'}</span>
              )}
            </Link>
          </div>

          <div className="nav-items">
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
              >
                <span className="nav-label">{item.label}</span>
                {item.path === '/users' && pendingUsersCount > 0 && (
                  <span className="nav-badge">{pendingUsersCount}</span>
                )}
              </Link>
            ))}
          </div>

          <div className="nav-user">
            <span className="user-name">
              Welcome, {getUserDisplayName()}
            </span>
            <button onClick={signOut} className="nav-signout">
              Sign Out
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <nav className="navigation-mobile">
        <div className="mobile-nav-header">
          <Link to="/" className="mobile-nav-brand">
            <img src="/1209-digital-logo.svg" alt="1209 Digital" className="mobile-company-logo" />
            <div className="mobile-divider" />
            {company?.branding?.logo ? (
              <img src={company.branding.logo} alt={company.name} className="mobile-nav-logo" />
            ) : (
              <span className="mobile-nav-company">{company?.name || 'Report Portal'}</span>
            )}
          </Link>

          <button
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="mobile-nav-menu">
            <div className="mobile-user-info">
              <span className="mobile-user-name">
                Welcome, {getUserDisplayName()}
              </span>
            </div>

            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`mobile-nav-item ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-nav-label">{item.label}</span>
              </Link>
            ))}

            <button
              onClick={() => {
                signOut();
                setMobileMenuOpen(false);
              }}
              className="mobile-nav-signout"
            >
              Sign Out
            </button>
          </div>
        )}
      </nav>

    </>
  );
};

export default Navigation;